// SB6-ANA-VIEW-05 — the Viewer's three floating controls, grouped into one
// draggable toolbar (Christian SB6 review 2026-05-26).
//
// Contents, left-to-right (Q1=a):
//   1. drag handle (grip) — the only grab surface (Q3=a), so dragging never
//      eats a button click;
//   2. "Create new defect" (DrawToolbar) — shown DISABLED, not hidden, on
//      LOCKED / SIGNED_OFF (Q2=b);
//   3. "Show/hide AI findings" + "Show/hide defects" (BBoxToggleControls).
//
// The toolbar starts top-left (Q4=a) so it no longer paints over the Radius
// ("distance from root") readout that lives top-right (ViewerReadouts). Its
// position is clamped inside the Viewer cell and persisted to localStorage
// (Q5=b). z-index sits above DrawOverlay (z10) so the Draw button stays
// clickable while a draw is in progress (AC6).
import { Box, Divider, Tooltip } from "@mui/material";
import { BBoxToggleControls } from "./BBoxToggleControls.js";
import { DrawToolbar } from "../components/DrawToolbar.js";
import { useDraggableWithinBounds } from "./useDraggableWithinBounds.js";
import { GripVerticalIcon } from "@aeriosiq/ui/icons";

/** Top-left default (Q4=a), matching the old 8px overlay inset. */
const DEFAULT_POSITION = { x: 8, y: 8 };
/** Per-browser persistence (Q5=b) — same `aero.*` convention as other prefs. */
const STORAGE_KEY = "aero.viewer.toolbar.position";

export interface ViewerToolbarProps {
  /**
   * Defect-editing capability for the active data acquisition
   * (`defectEditPermission.allowed`). When false (LOCKED / SIGNED_OFF —
   * SB6-ANA-VIEW-04), the Draw button renders disabled rather than hidden
   * (Q2=b); the bbox toggles are unaffected.
   */
  editAllowed: boolean;
  /** AI bbox colour from Settings → Bounding Boxes (`boundingBox.ai.color`). */
  aiColor: string;
  /** Defect bbox colour from Settings → Bounding Boxes (`boundingBox.defect.color`). */
  defectColor: string;
}

export function ViewerToolbar({
  editAllowed,
  aiColor,
  defectColor,
}: ViewerToolbarProps): React.ReactElement {
  const { position, isDragging, elementRef, onHandlePointerDown } =
    useDraggableWithinBounds({
      defaultPosition: { ...DEFAULT_POSITION },
      storageKey: STORAGE_KEY,
    });

  return (
    <Box
      ref={elementRef}
      role="toolbar"
      aria-label="Viewer controls"
      aria-orientation="horizontal"
      data-testid="viewer-toolbar"
      sx={{
        position: "absolute",
        left: position.x,
        top: position.y,
        // Above the OSD canvas + Konva overlay (1-2), the readouts (4), the
        // marquee (5) and the DrawOverlay (10) so every item stays clickable
        // — including the Draw button while a draw is in progress (AC6).
        zIndex: 12,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        p: 0.5,
        borderRadius: 1,
        backgroundColor: "background.paper",
        boxShadow: 3,
        // While dragging, suppress text selection + native touch scrolling so
        // the gesture stays smooth.
        userSelect: "none",
        touchAction: "none",
        ...(isDragging ? { cursor: "grabbing" } : {}),
      }}
    >
      <Tooltip title="Move toolbar">
        <Box
          component="button"
          type="button"
          aria-label="Move toolbar"
          data-testid="viewer-toolbar-handle"
          onPointerDown={onHandlePointerDown}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0.25,
            border: "none",
            borderRadius: 1,
            backgroundColor: "transparent",
            color: "text.secondary",
            cursor: isDragging ? "grabbing" : "grab",
            touchAction: "none",
            "&:hover": {
              color: "text.primary",
              backgroundColor: "action.hover",
            },
          }}
        >
          <GripVerticalIcon fontSize="small" />
        </Box>
      </Tooltip>
      <DrawToolbar editAllowed={editAllowed} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <BBoxToggleControls aiColor={aiColor} defectColor={defectColor} />
    </Box>
  );
}
